import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Mode, Session, Settings, TimerState } from '../types/pomodoro';
import { defaultSettings, MAX_DURATION, MIN_DURATION } from '../data/presets';
import { KEYS, load, remove, save } from '../utils/storage';
import { localDateKey } from '../utils/time';
import { playChime } from '../utils/chime';
interface PomodoroValue {
  settings: Settings;
  updateSettings: (patch: Partial<Settings>) => void;
  setDuration: (mode: Mode, minutes: number) => void;
  sessions: Session[];
  clearHistory: () => void;
  timer: TimerState;
  remainingMs: number;
  totalMs: number;
  sessionInProgress: boolean;
  start: () => void;
  pause: () => void;
  reset: () => void;
  selectMode: (mode: Mode) => void;
  task: string;
  setTask: (value: string) => void;
  message: string | null;
  dismissMessage: () => void;
  growthKey: number;
  goalReached: boolean;
  todayMinutes: number;
  todaySessions: number;
}
const PomodoroContext = createContext<PomodoroValue | null>(null);
function clampMinutes(minutes: number): number {
  if (!Number.isFinite(minutes)) return defaultSettings.durations.focus;
  return Math.min(MAX_DURATION, Math.max(MIN_DURATION, Math.round(minutes)));
}
function durationMs(settings: Settings, mode: Mode): number {
  return clampMinutes(settings.durations[mode]) * 60_000;
}
export function PomodoroProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [settings, setSettings] = useState<Settings>(() => {
    const stored = load<Settings>(KEYS.settings, defaultSettings);
    return {
      ...stored,
      durations: {
        ...defaultSettings.durations,
        ...stored.durations
      }
    };
  });
  const [sessions, setSessions] = useState<Session[]>(() => load<Session[]>(KEYS.sessions, []));
  const [task, setTask] = useState<string>(() => load<string>(KEYS.task, ''));
  const [timer, setTimer] = useState<TimerState>(() => load<TimerState>(KEYS.timer, {
    mode: 'focus',
    status: 'idle',
    endsAt: null,
    remainingMs: durationMs(settings, 'focus'),
    completedInCycle: 0
  }));
  const [remainingMs, setRemainingMs] = useState<number>(timer.status === 'running' && timer.endsAt ? Math.max(0, timer.endsAt - Date.now()) : timer.remainingMs);
  const [message, setMessage] = useState<string | null>(null);
  const [growthKey, setGrowthKey] = useState(0);
  const [goalReached, setGoalReached] = useState(false);
  const timerRef = useRef(timer);
  timerRef.current = timer;
  const settingsRef = useRef(settings);
  settingsRef.current = settings;
  const sessionsRef = useRef(sessions);
  sessionsRef.current = sessions;
  const handledRef = useRef<number | null>(null);
  useEffect(() => save(KEYS.settings, settings), [settings]);
  useEffect(() => save(KEYS.sessions, sessions), [sessions]);
  useEffect(() => save(KEYS.timer, timer), [timer]);
  useEffect(() => save(KEYS.task, task), [task]);
  const totalMs = durationMs(settings, timer.mode);
  const notify = useCallback((title: string, body: string) => {
    if (!settingsRef.current.notifications) return;
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    try {
      new Notification(title, {
        body
      });
    } catch {
      /* notifications unsupported in this context */
    }
  }, []);
  const finish = useCallback(() => {
    const prev = timerRef.current;
    if (prev.status !== 'running' || prev.endsAt === null) return;
    if (handledRef.current === prev.endsAt) return;
    handledRef.current = prev.endsAt;
    const current = settingsRef.current;
    if (current.soundEnabled) playChime();
    if (prev.mode === 'focus') {
      const minutes = clampMinutes(current.durations.focus);
      const session: Session = {
        id: `s-${prev.endsAt}`,
        endedAt: prev.endsAt,
        minutes
      };
      const today = localDateKey(Date.now());
      const before = sessionsRef.current.filter(s => localDateKey(s.endedAt) === today).reduce((sum, s) => sum + s.minutes, 0);
      setSessions(list => list.some(s => s.id === session.id) ? list : [...list, session]);
      const goal = current.dailyGoal;
      if (goal && before < goal && before + minutes >= goal) {
        setGoalReached(true);
        window.setTimeout(() => setGoalReached(false), 5000);
      }
      setGrowthKey(k => k + 1);
      const done = prev.completedInCycle + 1;
      const isLong = done >= current.longBreakInterval;
      const nextMode: Mode = isLong ? 'long' : 'short';
      const auto = current.autoStartBreaks;
      setTimer({
        mode: nextMode,
        status: auto ? 'running' : 'idle',
        endsAt: auto ? Date.now() + durationMs(current, nextMode) : null,
        remainingMs: durationMs(current, nextMode),
        completedInCycle: isLong ? 0 : done
      });
      setMessage('Your focus has grown.');
      notify('Focus session complete', isLong ? 'Time for a long break.' : 'Time for a short break.');
    } else {
      const auto = current.autoStartFocus;
      setTimer({
        mode: 'focus',
        status: auto ? 'running' : 'idle',
        endsAt: auto ? Date.now() + durationMs(current, 'focus') : null,
        remainingMs: durationMs(current, 'focus'),
        completedInCycle: prev.completedInCycle
      });
      setMessage('Break over. Ready when you are.');
      notify('Break complete', 'Ready for your next focus session.');
    }
  }, [notify]);

  /* Remaining time is always derived from the end timestamp, so throttled or
     suspended intervals (background tabs, sleep, refresh) cannot drift. */
  useEffect(() => {
    if (timer.status !== 'running' || timer.endsAt === null) {
      setRemainingMs(timer.remainingMs);
      return;
    }
    const endsAt = timer.endsAt;
    const tick = () => {
      const left = endsAt - Date.now();
      if (left <= 0) {
        setRemainingMs(0);
        finish();
        return;
      }
      setRemainingMs(left);
    };
    tick();
    const id = window.setInterval(tick, 250);
    const onVisible = () => tick();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [timer.status, timer.endsAt, timer.remainingMs, finish]);
  const start = useCallback(() => {
    setTimer(prev => {
      if (prev.status === 'running') {
        return {
          ...prev,
          status: 'paused',
          endsAt: null,
          remainingMs: Math.max(0, (prev.endsAt ?? 0) - Date.now())
        };
      }
      const left = prev.remainingMs > 0 ? prev.remainingMs : durationMs(settingsRef.current, prev.mode);
      return {
        ...prev,
        status: 'running',
        endsAt: Date.now() + left,
        remainingMs: left
      };
    });
    setMessage(null);
  }, []);
  const pause = useCallback(() => {
    setTimer(prev => prev.status === 'running' ? {
      ...prev,
      status: 'paused',
      endsAt: null,
      remainingMs: Math.max(0, (prev.endsAt ?? 0) - Date.now())
    } : prev);
  }, []);
  const reset = useCallback(() => {
    setTimer(prev => ({
      ...prev,
      status: 'idle',
      endsAt: null,
      remainingMs: durationMs(settingsRef.current, prev.mode)
    }));
    setMessage(null);
  }, []);
  const selectMode = useCallback((mode: Mode) => {
    setTimer(prev => ({
      ...prev,
      mode,
      status: 'idle',
      endsAt: null,
      remainingMs: durationMs(settingsRef.current, mode)
    }));
    setMessage(null);
  }, []);
  const setDuration = useCallback((mode: Mode, minutes: number) => {
    const value = clampMinutes(minutes);
    setSettings(prev => ({
      ...prev,
      durations: {
        ...prev.durations,
        [mode]: value
      }
    }));
    setTimer(prev => prev.mode === mode ? {
      ...prev,
      status: 'idle',
      endsAt: null,
      remainingMs: value * 60_000
    } : prev);
  }, []);
  const updateSettings = useCallback((patch: Partial<Settings>) => {
    setSettings(prev => ({
      ...prev,
      ...patch
    }));
  }, []);
  const clearHistory = useCallback(() => {
    setSessions([]);
    remove(KEYS.sessions);
  }, []);
  const todayKey = localDateKey(Date.now());
  const todaysSessions = useMemo(() => sessions.filter(s => localDateKey(s.endedAt) === todayKey), [sessions, todayKey]);
  const value: PomodoroValue = {
    settings,
    updateSettings,
    setDuration,
    sessions,
    clearHistory,
    timer,
    remainingMs,
    totalMs,
    sessionInProgress: timer.status !== 'idle' || remainingMs < totalMs,
    start,
    pause,
    reset,
    selectMode,
    task,
    setTask,
    message,
    dismissMessage: () => setMessage(null),
    growthKey,
    goalReached,
    todayMinutes: todaysSessions.reduce((sum, s) => sum + s.minutes, 0),
    todaySessions: todaysSessions.length
  };
  return <PomodoroContext.Provider value={value} data-id="element-85" data-mp="30kne3ekuv0ch">{children}</PomodoroContext.Provider>;
}
export function usePomodoro(): PomodoroValue {
  const ctx = useContext(PomodoroContext);
  if (!ctx) throw new Error('usePomodoro must be used inside PomodoroProvider');
  return ctx;
}